import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@app/api/auth/useAuth';
import { withAuthLang } from '@app/i18n';
import AuthLayout from '@shared/ui/auth/AuthLayout';
import { ThemeButton } from '@shared/ui/buttons';
import { PasswordInput } from '@shared/ui/forms/inputs';
import Note from '@shared/ui/messages/Note';
import { isEmptyObject } from '@utils/helpers';
import { passwordMatchRule, validateServerErrors } from '@utils/validation';

export default function ResetPasswordPage() {
  const [isValidateLoading, setIsValidateLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const { resetPassword, validateResetPasswordToken } = useAuth();
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: { password: '', password_confirmation: '' },
  });

  useEffect(() => {
    async function validateToken() {
      try {
        const res = await validateResetPasswordToken({ token, email });
        setIsTokenValid(res);
      } catch (error) {
        setIsTokenValid(false);
      } finally {
        setIsValidateLoading(false);
      }
    }
    validateToken();
  }, []);

  async function onSubmit(data) {
    try {
      await resetPassword(data, token, email);
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <AuthLayout>
      {!isValidateLoading &&
        (isTokenValid ? (
          <form className="auth__block" onSubmit={handleSubmit(onSubmit)}>
            <div className="block-pd-x block-pd-y">
              <h2 className="title-h2m">{t('new_password')}</h2>
              <p className="auth__subtitle">{t('new_password_subtitle')}</p>
              <Note
                html
                type="warning"
                title={t('password_requirements')}
                marginBottom
                content={t('password_rule')}
              />
              <div className="form">
                <PasswordInput
                  error={errors.password}
                  control={control}
                  name="password"
                  label={t('new_password')}
                  placeholder={t('enter_password')}
                  onValueChange={(newPassword) => {
                    if (
                      newPassword.trim() &&
                      getValues('password_confirmation').trim()
                    ) {
                      trigger('password_confirmation');
                    }
                  }}
                />
                <PasswordInput
                  control={control}
                  name="password_confirmation"
                  label={t('repeat_new_password')}
                  placeholder={t('enter_password')}
                  error={errors.password_confirmation}
                  validation={(value) =>
                    passwordMatchRule(value, getValues('password'))
                  }
                />
              </div>
            </div>
            <div className="dialog-actions btns-toolbar">
              <ThemeButton
                type="submit"
                startIcon="check"
                loading={isSubmitting}
                disabled={!isEmptyObject(errors)}
                minWidth
                block={false}
              >
                {t('submit')}
              </ThemeButton>
            </div>
          </form>
        ) : (
          <div className="auth__block">
            <div className="block-pd-x block-pd-y">
              <h2 className="title-h2m">{t('new_password')}</h2>
              <p>{t('password_reset_expired')}</p>
            </div>
            <div className="dialog-actions">
              <ThemeButton
                endIcon="chevron-right"
                onClick={() => navigate(withAuthLang('/forgot-password'))}
              >
                {t('back_to_forgot_password')}
              </ThemeButton>
            </div>
          </div>
        ))}
    </AuthLayout>
  );
}
